/**
 * API Utility for making HTTP requests with cache control
 */

// Cache for storing API responses
const apiCache = new Map();

/**
 * Make an API request with cache control
 * @param {string} url - API endpoint
 * @param {Object} options - Fetch options
 * @param {boolean} [forceRefresh=false] - Skip cache and force refresh
 * @returns {Promise<Object>}
 */
export async function apiRequest(url, options = {}, forceRefresh = false) {
    const cacheKey = `${url}:${JSON.stringify(options)}`;
    const now = Date.now();
    const CACHE_DURATION = 1000; // 1 second cache for API responses

    // Check cache if not forcing refresh
    if (!forceRefresh && apiCache.has(cacheKey)) {
        const { data, timestamp } = apiCache.get(cacheKey);
        if (now - timestamp < CACHE_DURATION) {
            console.log(`[API] Returning cached response for ${url}`);
            return data;
        }
    }

    try {
        // Add cache-busting parameter
        const cacheBuster = `_=${Date.now()}`;
        const separator = url.includes('?') ? '&' : '?';
        const requestUrl = `${url}${separator}${cacheBuster}`;

        // Default headers
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
            ...options.headers
        };

        const response = await fetch(requestUrl, {
            ...options,
            headers,
            credentials: 'same-origin',
            cache: 'no-store'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Update cache
        apiCache.set(cacheKey, {
            data,
            timestamp: now
        });

        return data;
    } catch (error) {
        console.error(`[API] Request failed for ${url}:`, error);
        
        // Return cached data if available (even if expired) when offline
        if (!navigator.onLine && apiCache.has(cacheKey)) {
            console.warn('[API] Offline - returning stale data');
            return apiCache.get(cacheKey).data;
        }
        
        throw error;
    }
}

/**
 * Clear API cache for specific URL or all URLs
 * @param {string} [url] - Optional URL to clear cache for
 */
export function clearApiCache(url) {
    if (url) {
        for (const key of apiCache.keys()) {
            if (key.startsWith(url)) {
                apiCache.delete(key);
            }
        }
    } else {
        apiCache.clear();
    }
}

/**
 * Make a GET request
 * @param {string} url - API endpoint
 * @param {Object} [params] - Query parameters
 * @param {boolean} [forceRefresh=false] - Skip cache
 * @returns {Promise<Object>}
 */
export function get(url, params = {}, forceRefresh = false) {
    const queryString = new URLSearchParams(params).toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return apiRequest(fullUrl, { method: 'GET' }, forceRefresh);
}

/**
 * Make a POST request
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise<Object>}
 */
export function post(url, data = {}) {
    return apiRequest(url, {
        method: 'POST',
        body: JSON.stringify(data)
    }, true); // Always force refresh after POST
}

/**
 * Make a PUT request
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise<Object>}
 */
export function put(url, data = {}) {
    return apiRequest(url, {
        method: 'PUT',
        body: JSON.stringify(data)
    }, true); // Always force refresh after PUT
}

/**
 * Make a DELETE request
 * @param {string} url - API endpoint
 * @returns {Promise<Object>}
 */
export function del(url) {
    return apiRequest(url, {
        method: 'DELETE'
    }, true); // Always force refresh after DELETE
}
